import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { CheckCircle, XCircle, Hourglass } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState("checking");
    const [tx, setTx] = useState(null);
    const navigate = useNavigate();
    const { refresh } = useAuth();

    useEffect(() => {
        if (!sessionId) { setStatus("error"); return; }
        let attempts = 0;
        // Poll for up to ~60s with a small backoff. The Stripe webhook
        // sometimes lands 10-30s after the user lands here, so being
        // patient prevents false "Payment not completed" toasts.
        const MAX_ATTEMPTS = 30;
        const poll = async () => {
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                setTx(data);
                if (data.payment_status === "paid") {
                    setStatus("paid");
                    refresh();
                    return;
                }
                if (data.status === "expired") { setStatus("expired"); return; }
                if (attempts++ >= MAX_ATTEMPTS) { setStatus("timeout"); return; }
                setTimeout(poll, 2000);
            } catch { setStatus("error"); }
        };
        poll();
    }, [sessionId, refresh]);

    return (
        <div className="min-h-screen grid place-items-center p-6" data-testid="payment-success-page">
            <div className="max-w-md w-full text-center rounded-2xl border border-border bg-card p-8">
                {status === "checking" && (
                    <>
                        <Hourglass size={48} weight="duotone" className="mx-auto text-[#6C63FF] mb-4 animate-pulse" />
                        <h1 className="font-display font-black text-2xl mb-2 brand-text">Verifying payment…</h1>
                        <p className="text-muted-foreground text-sm">This usually takes a few seconds.</p>
                    </>
                )}
                {status === "paid" && (
                    <>
                        <CheckCircle size={48} weight="fill" className="mx-auto text-emerald-500 mb-4" />
                        <h1 className="font-display font-black text-2xl mb-2 brand-text">Payment successful</h1>
                        <p className="text-muted-foreground text-sm mb-6">Thanks for supporting Ideas Mine! Your plan/boost is now active.</p>
                        <Button onClick={() => navigate("/")} className="brand-button w-full" data-testid="back-home">Back home</Button>
                    </>
                )}
                {(status === "expired" || status === "timeout" || status === "error") && (
                    <>
                        <XCircle size={48} weight="fill" className="mx-auto text-red-500 mb-4" />
                        <h1 className="font-display font-black text-2xl mb-2 brand-text">
                            {status === "timeout" ? "We're confirming your payment" : "Payment not completed"}
                        </h1>
                        {status === "timeout" ? (
                            <>
                                <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
                                    If your card was charged, your plan will activate automatically the moment we sync with Stripe. This sometimes takes a few minutes.
                                </p>
                                {tx?.poll_error && (
                                    <div className="text-xs bg-red-500/10 border border-red-500/30 text-red-500 rounded-md p-3 mb-4 text-left">
                                        <div className="font-bold mb-1">Stripe sync error (share with admin):</div>
                                        <div className="font-mono break-all">{tx.poll_error}</div>
                                    </div>
                                )}
                                <p className="text-muted-foreground text-xs mb-6 leading-relaxed">
                                    Still stuck after 10 minutes? Email{" "}
                                    <a href="mailto:support@ideasmine.co.uk" className="text-[#6C63FF] font-semibold">support@ideasmine.co.uk</a>{" "}
                                    with your Stripe receipt — we'll activate it manually.
                                </p>
                                <Link to="/">
                                    <Button variant="outline" className="w-full" data-testid="back-home">
                                        Continue browsing — we'll email you when it activates
                                    </Button>
                                </Link>
                            </>
                        ) : (
                            <>
                                <p className="text-muted-foreground text-sm mb-6">
                                    Please try again from the Subscription page.
                                </p>
                                <Link to="/subscription">
                                    <Button variant="outline" className="w-full">Back to plans</Button>
                                </Link>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
